/**
 * `pending-public-status-page` v0 — wire contracts.
 *
 * Public surface (no auth):
 *   GET /api/status/public → PublicStatusResponse
 *
 * Admin surface (admin-guarded):
 *   GET    /api/admin/status/incidents              → { count, incidents }
 *   POST   /api/admin/status/incidents              ← CreateStatusIncidentRequest
 *   GET    /api/admin/status/incidents/:id          → { incident }
 *   PATCH  /api/admin/status/incidents/:id          ← UpdateStatusIncidentRequest
 *   DELETE /api/admin/status/incidents/:id          → { ok: true }
 */
import { z } from 'zod';

export const StatusIncidentStateSchema = z.enum(['investigating', 'identified', 'monitoring', 'resolved']);
export type StatusIncidentState = z.infer<typeof StatusIncidentStateSchema>;

export const StatusIncidentSeveritySchema = z.enum(['minor', 'major', 'critical']);
export type StatusIncidentSeverity = z.infer<typeof StatusIncidentSeveritySchema>;

export const StatusOverallSchema = z.enum(['operational', 'degraded', 'outage']);
export type StatusOverall = z.infer<typeof StatusOverallSchema>;

export const StatusIncidentSchema = z.object({
  id: z.string().uuid(),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(20_000),
  status: StatusIncidentStateSchema,
  severity: StatusIncidentSeveritySchema,
  components: z.array(z.string().min(1).max(64)).max(32),
  createdBy: z.string().uuid().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  resolvedAt: z.string().nullable(),
});
export type StatusIncident = z.infer<typeof StatusIncidentSchema>;

export const CreateStatusIncidentRequestSchema = z.object({
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(20_000),
  status: StatusIncidentStateSchema.default('investigating'),
  severity: StatusIncidentSeveritySchema.default('minor'),
  components: z.array(z.string().min(1).max(64)).max(32).default([]),
});
export type CreateStatusIncidentRequest = z.infer<typeof CreateStatusIncidentRequestSchema>;

export const UpdateStatusIncidentRequestSchema = z
  .object({
    title: z.string().min(1).max(200).optional(),
    body: z.string().min(1).max(20_000).optional(),
    status: StatusIncidentStateSchema.optional(),
    severity: StatusIncidentSeveritySchema.optional(),
    components: z.array(z.string().min(1).max(64)).max(32).optional(),
  })
  .refine((p) => Object.keys(p).length > 0, { message: 'At least one field must be provided.' });
export type UpdateStatusIncidentRequest = z.infer<typeof UpdateStatusIncidentRequestSchema>;

export const ComponentStatusSchema = z.object({
  name: z.string().min(1),
  status: StatusOverallSchema,
});
export type ComponentStatus = z.infer<typeof ComponentStatusSchema>;

export const PublicStatusResponseSchema = z.object({
  overall: StatusOverallSchema,
  components: z.array(ComponentStatusSchema),
  active: z.array(StatusIncidentSchema),
  recent: z.array(StatusIncidentSchema),
  generatedAt: z.string(),
});
export type PublicStatusResponse = z.infer<typeof PublicStatusResponseSchema>;

export const ListStatusIncidentsResponseSchema = z.object({
  count: z.number().int().nonnegative(),
  incidents: z.array(StatusIncidentSchema),
});
export type ListStatusIncidentsResponse = z.infer<typeof ListStatusIncidentsResponseSchema>;
